import { useEffect, useRef } from "react";
import {
  AgentInputProto,
  ChatClientType,
  chatClientTypeToJSON,
  ChatContext,
  ChatRequest,
  ChatResponse,
} from "build-ts/chat_session";
import { ChatMessageType, chatMessageTypeFromJSON, DarwinAgentType, DarwinHeader } from "build-ts/common";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import type { AnalystProto } from "build-ts/analyst";
import { initializeChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";
import { generateRandomSuffix } from "utils/nameUtils";
/**
 * Matches any artifact tag a user can produce or mention in the chat input:
 *   [[workflow: darwinName|Display Name]]
 *   [[dataframe: table_name]]
 *   [[chart: chart_name]]
 *   [[code: snippet_name]]
 *   [[report: report_name]]
 * These tags are semantic markers sent to the agent as-is.
 * In the editor they are rendered as styled non-editable chips (MessageInput).
 * In sent messages they are rendered as clickable ArtifactLink chips (Message).
 *
 * --- Workflow tag format note ---
 * Workflow tags use "darwinName|Display Name" as the body instead of just the
 * darwin name. This is because:
 *   - The darwin name is the machine key used at runtime to look up the artifact.
 *   - The display name (header.name) is the human-readable label shown in chips.
 *   - Since buildNodes (MessageInput) has no access to the artifact map at parse
 *     time, encoding both in the tag body is the only way to show the correct
 *     label both during input and when re-rendering a saved message from the DB.
 * Consumers MUST split on "|" to extract the darwin name before using it for
 * lookups (see Message.tsx and MessageInput.tsx).
 *
 * Group 1 → tag type  (workflow | dataframe | chart | code | report)
 * Group 2 → tag body  (everything between the type colon and closing ]])
 */
export const ARTIFACT_TAG_TYPE = {
  workflow: "workflow",
  workflowRun: "workflow-run",
  workflowRunDetail: "workflow-run-detail",
  workflowRunSearch: "workflow-run-search",
  dataframe: "dataframe",
  chart: "chart",
  code: "code",
  report: "report",
  /** Internal bucket key for FILE_ARTIFACT — LLM references uploads via [[dataframe:name]]. */
  file: "file",
} as const;

export const ARTIFACT_TAG_RE = /\[\[(workflow|dataframe|chart|code|report):\s*([^\]]+)\]\]/gi;

/**
 * Build a canonical workflow tag string.
 *
 * @param lookupKey  The darwin_name (or header.id as fallback) used by the agent and
 *                   findWorkflowArtifactByDarwinName for runtime resolution.
 * @param displayName  The human-readable label shown in chips. When omitted the tag
 *                     uses the simple single-part format used by agent-generated tags.
 *
 * Examples:
 *   buildWorkflowTag("daily_cpl_brazil")
 *     → "[[workflow: daily_cpl_brazil]]"
 *   buildWorkflowTag("daily_cpl_brazil", "Daily CPL — Brazil")
 *     → "[[workflow: daily_cpl_brazil|Daily CPL — Brazil]]"
 */
export function buildWorkflowTag(lookupKey: string, displayName?: string): string {
  const key = lookupKey.trim();
  const name = displayName?.trim();
  return name ? `[[${ARTIFACT_TAG_TYPE.workflow}: ${key}|${name}]]` : `[[${ARTIFACT_TAG_TYPE.workflow}: ${key}]]`;
}

/**
 * Strip the |displayName suffix from workflow tags before the message is sent
 * to the AI. The display name is purely a frontend rendering aid and should not
 * be included in the text that reaches Darwin.
 *
 * Priority: left part (darwin_name / lookup key) if non-empty; otherwise fall
 * back to the right part (display name) so something meaningful still reaches Darwin.
 *
 *   "[[workflow: daily_cpl|Daily CPL — Brazil]]"  →  "[[workflow: daily_cpl]]"
 *   "[[workflow: |Daily CPL — Brazil]]"            →  "[[workflow: Daily CPL — Brazil]]"
 *   "[[workflow: daily_cpl]]"                      →  unchanged
 */
export function normalizeWorkflowTagsForSend(message: string): string {
  return message.replace(/\[\[workflow:\s*([^|\]]*)\|([^\]]*)\]\]/gi, (_, key, displayName) => {
    const k = key.trim();
    const d = displayName.trim();
    return `[[${ARTIFACT_TAG_TYPE.workflow}: ${k || d}]]`;
  });
}

export type MatchResult = { matched: boolean; score: number; ranges: [number, number][] };

/**
 * Check whether a search query matches a label using a single-pass subsequence scan.
 *
 * Every character in the query must appear in the label in order, but gaps are allowed —
 * this is a strict superset of substring matching. Examples:
 *   "channel"    → "analyze_channel_performance"  ✓ (contiguous run)
 *   "channelperf"→ "analyze_channel_performance"  ✓ (non-contiguous subsequence)
 *   "brazilcpl"  → "brazil_leads_cpl_last_week"   ✓ (characters span multiple tokens)
 *   "bgc"        → "background_capacity"          ✓ (b=background[0], g=background[4], c=capacity[0])
 *
 * Scoring rewards two things so that better matches sort above weaker ones:
 *   - Consecutive matched characters  (+2 per character in the run, compounding)
 *   - Matches that land on a word boundary  (+5 per hit)
 *
 * Word boundaries are detected at: the start of the string, after any _ or space
 * separator, and at camelCase transitions (lowercase → uppercase).
 *
 * The returned `ranges` are contiguous [start, end] index pairs (inclusive, on the
 * lowercased label) of matched character runs — useful for highlighting in the UI.
 */
export function labelMatchesQuery(label: string, query: string): MatchResult {
  if (!query) return { matched: true, score: 0, ranges: [] };

  const l = label.toLowerCase();
  const q = query.toLowerCase();

  // Build the set of word-boundary positions.
  const boundaries = new Set<number>();
  boundaries.add(0);
  for (let i = 1; i < l.length; i++) {
    // After a separator (underscore or space).
    if (/[_\s]/.test(l[i - 1])) boundaries.add(i);
    // camelCase transition: previous char is lowercase, current char in the original
    // label is uppercase (check `label[i]`, not `l[i]`, to preserve case information).
    if (/[a-z]/.test(l[i - 1]) && /[A-Z]/.test(label[i])) boundaries.add(i);
  }

  let qi = 0;
  let score = 0;
  let consecutive = 0;
  const ranges: [number, number][] = [];
  let rangeStart = -1;

  for (let li = 0; li < l.length && qi < q.length; li++) {
    if (l[li] === q[qi]) {
      if (rangeStart === -1) rangeStart = li;
      consecutive++;
      score += consecutive * 2; // compounding bonus for contiguous runs
      if (boundaries.has(li)) score += 5; // extra reward for hitting a word boundary
      qi++;
    } else {
      if (rangeStart !== -1) {
        ranges.push([rangeStart, li - 1]);
        rangeStart = -1;
      }
      consecutive = 0;
    }
  }

  // Close any open range that reaches the end of the label.
  if (rangeStart !== -1) ranges.push([rangeStart, l.length - 1]);

  return { matched: qi === q.length, score, ranges };
}

/**
 * Resolves the Darwin agent type from the REACT_APP_AGENT_TYPE_FOR_CHAT env
 * var, falling back to ANALYST_CHAT_AGENT when the var is absent or empty.
 */
export function getAgentTypeFromEnv(): DarwinAgentType {
  const envAgentType = process.env.REACT_APP_AGENT_TYPE_FOR_CHAT;
  return envAgentType !== undefined && envAgentType !== null && envAgentType !== ""
    ? (Number(envAgentType) as DarwinAgentType)
    : DarwinAgentType.ANALYST_CHAT_AGENT;
}

/**
 * Fetches all scheduled jobs owned by the given analyst and returns only the
 * SCENARIO_JOB-typed ones. Returns an empty array when `analystId` is falsy
 * or no jobs are found.
 */
export async function loadScenarioJobs(analystId: string | undefined): Promise<ScheduledJobProto[]> {
  if (!analystId) return [];
  const jobs = await listScheduledJobsByOwnerId(analystId, true);
  if (!jobs || jobs.length === 0) return [];
  return jobs.filter((j: ScheduledJobProto) => j.type === ScheduledJobType.SCENARIO_JOB);
}

/**
 * Fires the naming agent in the background (non-blocking).
 *
 * On success: persists the generated title via `updateChatSessionHeaders`,
 * dispatches the `chat:title-updated` window event so the navbar refreshes,
 * and calls `onTitle` so the caller can apply the title to its own React state.
 *
 * Errors are swallowed and logged as warnings — naming is non-critical.
 */
export function fireNamingAgent(
  msg: string,
  sessionId: string,
  userId: string,
  onTitle: (title: string) => void
): void {
  initializeChatSession(
    ChatRequest.create({
      session_header: DarwinHeader.create({
        id: "",
        type: chatClientTypeToJSON(ChatClientType.FRIDA),
        author_id: userId,
      }),
      chat_context: ChatContext.create({ agent_type: DarwinAgentType.CHAT_NAMING_AGENT }),
      message: msg,
      is_save: false,
    })
  )
    .then(resp => {
      const titleMsg = resp?.messages?.find(
        m => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.RESPONSE && m.content
      );
      const generatedTitle = titleMsg?.content?.trim();
      if (!generatedTitle) return;

      onTitle(generatedTitle);
      updateChatSessionHeaders([sessionId], generatedTitle)
        .then(() => {
          window.dispatchEvent(new CustomEvent("chat:title-updated", { detail: { sessionId, title: generatedTitle } }));
        })
        .catch(err => console.warn("Failed to persist generated title:", err));
    })
    .catch(err => console.warn("Session naming failed (non-critical):", err));
}

/**
 * Creates and persists a new chat session in Darwin DB.
 *
 * @param userId - The current user's ID.
 * @param analystDetails - The resolved analyst proto.
 * @param scenarioJobs - Scenario jobs to embed in the session context.
 * @param agentType - The Darwin agent type to use (see getAgentTypeFromEnv).
 * @param isSave - Whether to persist the session: always `true` for Frida;
 *                 use `isFromEva ? isChatSaved : true` for Eva so that unsaved
 *                 Eva chats are not wrongly written to the DB.
 * @returns The new `sessionId`, or `null` if creation failed.
 */
export async function createChatSession(
  userId: string,
  analystDetails: AnalystProto,
  scenarioJobs: ScheduledJobProto[],
  agentType: DarwinAgentType,
  isSave: boolean
): Promise<ChatResponse | null> {
  const chatContext = ChatContext.create({
    agent_type: agentType,
    agent_input: AgentInputProto.create({
      analyst: analystDetails,
      scenario_jobs: scenarioJobs,
    }),
  });

  const resp = await initializeChatSession(
    ChatRequest.create({
      session_header: DarwinHeader.create({
        id: "",
        name: `Chat ${generateRandomSuffix(8)}`,
        type: chatClientTypeToJSON(ChatClientType.FRIDA),
        author_id: userId,
      }),
      chat_context: chatContext,
      is_save: isSave,
      stream_response: false,
    })
  );

  return resp ?? null;
}

// Auto-send hook

/**
 * Fires `onSend` exactly once when `prompt` is non-empty and `ready` is true.
 *
 * A ref guards against double-sends across re-renders (e.g. analyst list loading
 * in or isLoading flipping) — the same prompt string is never dispatched twice.
 *
 * @param prompt  The message to auto-send (undefined/empty = hook is inactive).
 * @param ready   All pre-conditions are met (analysts loaded, not already loading, etc.).
 * @param onSend  Callback that receives the trimmed prompt. Must be stable (wrap in useCallback).
 * @returns       `true` while the prompt is set but has not yet been sent (useful for
 *                showing a loading indicator).
 */
export function useAutoSend(
  prompt: string | null | undefined,
  ready: boolean,
  onSend: (prompt: string) => void
): boolean {
  const lastSentRef = useRef<string | null>(null);

  // Wrap onSend in a stable ref so changing the callback never re-triggers the effect.
  const onSendRef = useRef(onSend);
  useEffect(() => {
    onSendRef.current = onSend;
  });

  useEffect(() => {
    if (!prompt?.trim() || !ready || lastSentRef.current === prompt) return;
    lastSentRef.current = prompt;
    onSendRef.current(prompt.trim());
  }, [prompt, ready]);

  return !!prompt?.trim() && lastSentRef.current !== prompt;
}
